<template>
  <div
    v-if="videoUrl"
    data-qa-is-lpu
    class="video-card video-card-vue d-inline-flex"
  >
    <slot
      name="play-button"
      :on="{
        click: handleOpenDialog,
      }"
    >
      <div
        v-ripple
        data-qa="video_card_play_icon"
        class="video-card__play-icon d-flex align-center cursor-pointer bg-gray-0"
        :class="{ 'video-card__play-icon--mobile': isMobile }"
        @click="handleOpenDialog"
      >
        <v-img
          :alt="tStatic('Видеовизитка')"
          :width="isMobile ? 24 : 28"
          :height="isMobile ? 24 : 28"
          src="/static/_v1/pd/icons/ui-kit/video-card-play.svg"
        />
        <div
          v-if="!isMobile"
          class="text--text text-body-2"
        >
          {{ tStatic('Видеовизитка') }}
        </div>
      </div>
    </slot>
    <v-dialog
      v-if="isMobile"
      v-model="isDialogVisible"
      data-qa="video_card_dialog"
      fullscreen
      divider
    >
      <div
        class="video-card-wrapper text"
        data-qa="video_card_dialog"
      >
        <div
          class="video-card-player-close ui-icon_reset bg-gray-0--text ui-icon-close-not-a-circle"
          @click="isDialogVisible = false"
        />
        <div
          class="video-card-switch-state"
          @click="videoSwitchState"
        />
        <div
          v-if="!isVideoPlaying"
          class="video-card-play primary--text branding-btn branding-btn--primary-icon"
        >
          <div class="ui-icon_reset ui-icon-player-play" />
        </div>
        <div
          v-if="videoTimeTotal && videoTimeCurrent"
          class="video-card-time-cover"
        >
          <div class="video-card-time">
            <div
              class="text-body-1 bg-gray-0--text"
            >
              {{ videoTimeCurrent }}
            </div>
            <span class="text-body-1 bg-gray-60--text px-1">/</span>
            <div
              class="text-body-1 bg-gray-60--text"
            >
              {{ videoTimeTotal }}
            </div>
          </div>
        </div>
        <video
          ref="videoRef"
          data-qa="video_card_video"
          playsinline
          class="video-card-player"
          :src="videoUrl"
          @timeupdate="handleTimeupdate"
          @loadedmetadata="handleLoadedmetadata"
          @ended="handleEnded"
        />
      </div>
    </v-dialog>
    <CoreDialog
      v-else
      v-model="isDialogVisible"
      data-qa="video_card_dialog"
      size="lg"
      :title="tStatic('Видеовизитка')"
      divider
    >
      <template #prependBtn="{ btnProps }">
        <v-btn
          v-bind="btnProps"
          @click="isDialogVisible = false"
        >
          <span class="ui-icon-close-not-a-circle" />
        </v-btn>
      </template>
      <video
        ref="videoRef"
        data-qa="video_card_video"
        controls
        class="video-card-player video-card-player--desktop h-100"
        :src="videoUrl"
        @ended="handleEnded"
      />
    </CoreDialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, nextTick, watch } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import useYandexGoals from 'src/composables/useYandexGoals'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import apiLpuVideoCardViewCount from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/HeroSection/components/VideoCard/api/apiLpuVideoCardViewCount'
import dispatchCustomEventGlobally from 'www/utils/withDependences/dispatchCustomEventGlobally'

const props = defineProps<{
  videoUrl: string
  videoUuid: string
}>()

const timeoutId = ref<ReturnType<typeof setTimeout>>()
const videoTimeTotal = ref('0')
const videoTimeCurrent = ref('0')
const isVideoPlaying = ref(false)
const isDialogVisible = ref(false)
const { sendYaGoal } = useYandexGoals()
const isMobile = useIsMobile()

const videoRef = ref()

function handleCloseDialog() {
  isDialogVisible.value = false
  isVideoPlaying.value = true

  videoSwitchState()

  nextTick(() => {
    if (videoRef.value) {
      const diff = videoRef.value.duration - videoRef.value.currentTime

      if (diff !== 0) { // значит видео НЕ досмотрено до конца
        sendYaGoal('common_LpuPage_VideoCard__closeWithoutView')
      }

      videoRef.value.currentTime = 0

      // TODO: Нужно для бокового меню. Возможно нет смысла в этом событии. Проверить.
      dispatchCustomEventGlobally('aside-sizes:update')
    }
  })

  // Костыль, который снижает z-index кнопке. Иначе она может перекрыть собой иные открытые окна.
  // Модальное окно должно иметь высокий z-index когда открыто, иначе иные элементы на странице могут перекрыть видео
  timeoutId.value = setTimeout(() => { // Задержка нужна для десктопной версии, чтобы поиск не мигал при закрытии модального окна с видео
    document.querySelectorAll<HTMLDivElement>('.video-card-vue')
      .forEach(node => { node.style.zIndex = '5' })
  }, 500)
}

async function handleOpenDialog() {
  isDialogVisible.value = true

  if (timeoutId.value) { // Если пользователь быстро закроет и откроет окно - z-index будет некорректным из-за setTimeout выше. Поэтому исполнение сброса z-index необходимо обнулить
    clearTimeout(timeoutId.value)
  }

  // Костыль, который повышает z-index кнопке.
  // Модальное окно должно иметь высокий z-index когда открыто, иначе иные элементы на странице могут перекрыть видео
  document.querySelectorAll<HTMLDivElement>('.video-card-vue')
    .forEach(node => { node.style.zIndex = '100' })

  videoSwitchState()
  sendYaGoal('common_LpuPage_VideoCard__play')

  try {
    if (!props.videoUuid) {
      return
    }

    await apiLpuVideoCardViewCount.post(props.videoUuid)
  } catch { /* Empty */ }
}

watch(isDialogVisible, value => {
  if (!value) {
    handleCloseDialog()
  }
})

function videoSwitchState() {
  nextTick(() => {
    if (!videoRef.value) {
      return
    }

    if (isVideoPlaying.value) {
      isVideoPlaying.value = false
      videoRef.value.pause()
    } else {
      isVideoPlaying.value = true
      videoRef.value.play()
    }
  })
}

function handleTimeupdate() {
  videoTimeCurrent.value = convertSeconds(videoRef.value.currentTime)
}

function handleLoadedmetadata() {
  videoTimeTotal.value = convertSeconds(videoRef.value.duration)
  videoTimeCurrent.value = convertSeconds(videoRef.value.currentTime)
}

function handleEnded() {
  sendYaGoal('common_LpuPage_VideoCard__finish')
}

function convertSeconds(sec: string | number) {
  const date = new Date(1970, 0, 0, 0, 0, Number(sec) || 0)
    .toLocaleTimeString('ru')
    .split(':')

  return `${Number(date[1])}:${date[2]}`
}

defineExpose({
  openDialog: handleOpenDialog,
})
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.video-card {
  &__play-icon {
    column-gap: 10px;
    border-radius: 24px;
    padding: 12px;
    box-shadow: 0 1.23px 2.46px 0 rgba(5, 23, 40, 0.08),
      0 0 2.46px 0 rgba(21, 40, 92, 0.06),
      0 2.46px 29.5px 0 rgba(9, 42, 72, 0.08);

    &--mobile {
      padding: 2px;
    }
  }

  &-wrapper {
    position: relative;
  }

  &-player {
    width: 100%;
    height: 100vh;
    display: block;

    @supports (height: 100dvh) {
      height: 100dvh;
    }

    &--desktop {
      max-height: 743px;
    }
  }

  &-play {
    z-index: 1;
    width: 40px;
    height: 40px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: calc(50% - 20px);
    left: calc(50% - 20px);
    background-color: $ui-kit-bg-primary;
    border-radius: $border-radius-circle;
  }

  &-player-close {
    top: 16px;
    left: 8px;
    z-index: 5;
    position: absolute;
  }

  &-switch-state {
    width: 100%;
    left: 0;
    bottom: 0;
    z-index: 5;
    position: absolute;
    height: calc(100% - 50px);
  }

  &-time-cover {
    width: 100%;
    left: 0;
    bottom: 100px;
    position: absolute;
    display: flex;
    justify-content: center;
  }

  &-time {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: $border-radius-md;
    background: rgba($ui-kit-text, 0.8);
  }
}
</style>
